// S-01 - Login — look & feel Planorama
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiRequestError, authApi } from "../api/api";
import { useAuth } from "../context/AuthContext";

type LoginConfig = {
  authMode: "local" | "sso";
  appEnv: string;
  showDevHint: boolean;
  hint?: { identifier: string; note: string };
};

/** Fallback si l'API est down — pas de mot de passe (secret hors API). */
const DEV_FALLBACK_HINT = {
  identifier: "DEVADMIN",
};

export function LoginPage() {
  const { t } = useTranslation();
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState<LoginConfig | null>(null);

  useEffect(() => {
    void authApi
      .loginConfig()
      .then((cfg) => {
        setConfigError(null);
        setConfig(cfg);
        if (cfg.showDevHint && cfg.hint) {
          setIdentifier(cfg.hint.identifier);
        }
      })
      .catch(() => {
        setConfigError(t("login_api_unreachable"));
        if (import.meta.env.DEV) {
          setConfig({
            authMode: "local",
            appEnv: "development",
            showDevHint: true,
            hint: { identifier: DEV_FALLBACK_HINT.identifier, note: t("login_hint_fallback") },
          });
          setIdentifier(DEV_FALLBACK_HINT.identifier);
        } else {
          setConfig({ authMode: "local", appEnv: "unknown", showDevHint: false });
        }
      });
  }, [t]);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.code === "ACCOUNT_LOCKED") {
          setError(t("login_locked"));
        } else if (err.code === "AUTH_SSO_REQUIRED") {
          setError(t("login_sso_required"));
        } else {
          setError(t("login_bad_credentials"));
        }
      } else {
        setError(t("login_connect_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isSso = config?.authMode === "sso";

  return (
    <div className="sm-login">
      <div className="sm-login-card">
        <div className="sm-login-brand">
          <h1>{t("app_name")}</h1>
          <p>{t("login_subtitle")}</p>
        </div>

        {configError ? (
          <div className="sm-alert sm-alert-error" role="alert">
            {configError}
          </div>
        ) : null}

        {config?.showDevHint && config.hint ? (
          <div className="sm-login-dev-hint" role="note">
            <strong>{t("login_dev_only")}</strong>
            <div>
              {t("login_user")} <code>{config.hint.identifier}</code>
            </div>
            <p className="sm-muted">{t("login_dev_hint_note")}</p>
          </div>
        ) : null}

        {isSso ? (
          <div className="sm-login-form">
            <p className="sm-muted" style={{ margin: 0 }}>
              {t("login_sso_body")}
            </p>
            <button
              className="sm-btn sm-btn-primary sm-btn-block"
              type="button"
              disabled
              title={t("login_sso_title")}
            >
              <LogIn size={15} aria-hidden />
              {t("login_sso_soon")}
            </button>
            {error ? (
              <div className="sm-alert sm-alert-error" role="alert">
                {error}
              </div>
            ) : null}
          </div>
        ) : (
          <form className="sm-login-form" onSubmit={onSubmit}>
            <label className="sm-label">
              {t("login_identifier")}
              <input
                className="sm-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t("login_identifier_ph")}
                autoComplete="username"
                required
              />
            </label>

            <label className="sm-label">
              {t("login_password")}
              <div className="sm-input-wrap">
                <input
                  className="sm-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="sm-input-eye"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? t("login_hide_password") : t("login_show_password")}
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </label>

            {error ? (
              <div className="sm-alert sm-alert-error" role="alert">
                {error}
              </div>
            ) : null}

            <button
              className="sm-btn sm-btn-primary sm-btn-block"
              type="submit"
              disabled={submitting || !identifier || !password}
            >
              <LogIn size={15} aria-hidden />
              {submitting ? t("login_submitting") : t("login_submit")}
            </button>
          </form>
        )}

        <p className="sm-login-foot">{t("login_footer")}</p>
      </div>
    </div>
  );
}
